import { CalendarEvent, CalendarService } from 'src/app/core/providers/calendar/calendar.service';
import { planningDateRange, planningLocalDay } from 'src/app/core/helpers/planning-time.helpers';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalContainerComponent } from 'src/app/shared/modal-container/modal-container.component';
import { CommentFieldComponent } from 'src/app/pages/comment-field/comment-field.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { FormDataApiService } from 'src/app/core/providers/apiFormData/FormDataApiService';
import {
  NgbDateParserFormatter,
  NgbDatepickerModule,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from 'src/app/core/providers/date/CustomDateParserFormatter';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CreateTaskTimeQueryParams,
  PlanningTask,
  TaskActor,
  TaskActorListResponse,
  TaskFormPlanningDto,
  TaskTimeListResponse,
  UpdateTaskTimeQueryParams,
} from 'src/app/core/providers/planning/planning.models';
import {
  datePickerToDateString,

} from 'src/app/core/helpers/date.helpers';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/core/providers/api/api.service';
import { AuthenticationService } from 'src/app/core/providers/auth/auth.service';
import { ToastService } from 'src/app/core/providers/toast/toast.service';
import { BulkOperationService } from 'src/app/core/providers/bulk-operation/bulk-operation.service';
import { HttpContext } from '@angular/common/http';
import { SKIP_SPINNER } from 'src/app/core/interceptors/spinner.Interceptor';
import { TaskTimeDisplayService } from 'src/app/core/providers/task-time-display/task-time-display.service';

@Component({
  selector: 'app-duration-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ModalContainerComponent,
    CommentFieldComponent,
    ReactiveFormsModule,
    NgbDatepickerModule,
  ],
  templateUrl: './duration-modal.component.html',
  styleUrl: './duration-modal.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class DurationModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() calendarEvent?: CalendarEvent;
  calendarComment = '';
  @Input() task!: TaskFormPlanningDto;
  @Input() planningTask: PlanningTask | null = null; // Used for update
  @Input() isListViewMode: boolean = false;
  @Output() sucessTaskTimeCreation = new EventEmitter<any>();
  @Input() taskTimePlannedId: string = '';

  // Mode mise à jour en masse
  @Input() isBulkMode: boolean = false;
  @Input() bulkFilters: any = null;
  /** IDs directs à traiter (sélection manuelle). Si null → utilise bulkFilters pour récupérer via API. */
  @Input() bulkTaskIds: string[] | null = null;

  // État bulk
  bulkIds: string[] = [];
  bulkIdsLoaded: boolean = false;
  isFetchingIds: boolean = false;
  bulkProgress: number = 0;
  bulkTotal: number = 0;
  bulkErrors: string[] = [];
  isBulkProcessing: boolean = false;
  /** Code de l'action en cours (pour afficher le spinner sur le bon bouton uniquement) */
  currentBulkActionCode: string | null = null;

  // Commentaire en masse
  bulkCommentText: string = '';
  isBulkCommentExpanded: boolean = false;
  isBulkCommentPosting: boolean = false;
  bulkCommentProgress: number = 0;
  bulkCommentErrors: string[] = [];

  form: FormGroup;
  submitted: boolean = false;
  isSaving: boolean = false;
  subscription: Subscription = new Subscription();
  currentUserTaskActor: TaskActor | null = null;
  isCurrentUserSupervisor: boolean = false;
  taskActors: TaskActor[] = [];
  executorRoleId: string = '';
  plannedTaskTime: any = null; // Temps planifié actuel
  originalExecutor: TaskActor | null = null; // Ancien assigné (EXECUTOR)
  isDescriptionExpanded = false;
  isCommentsExpanded = false;
  customStatuses: any[] = []; // Statuts personnalisés avec firm défini
  baseStatuses: any[] = [];
  allStatuses: any[] = []; // Tous les statuts triés par filter pour respecter l'ordre global
  private statusOrder: Map<string, number> = new Map();
  private isInitialized: boolean = false;

  private standardStatusButtonsConfig: Record<
    string,
    {
      labelKey: string;
      type: 'submit' | 'toggleNotApplicable';
      submitStatus?: string;
    }
  > = {
    PARTIAL: { labelKey: 'durationModal.inProgress', type: 'submit', submitStatus: 'PARTIAL' },
    TO_SUPERVISE: { labelKey: 'durationModal.toSupervise', type: 'submit', submitStatus: 'TO_SUPERVISE' },
    SUPERVISED: { labelKey: 'durationModal.supervised', type: 'submit', submitStatus: 'SUPERVISED' },
    ACTUAL: { labelKey: 'durationModal.done', type: 'submit', submitStatus: 'ACTUAL' },
    NOT_APPLICABLE: { labelKey: 'durationModal.notApplicable', type: 'toggleNotApplicable' },
  };

  private getStandardStatusOrderIndex(code: string): number {
    return this.statusOrder.get(code) ?? 999;
  }

  getStandardButtons(): Array<{
    code: string;
    labelKey: string;
    type: 'submit' | 'toggleNotApplicable';
    submitStatus?: string;
  }> {
    const baseCodes = ['PARTIAL', 'TO_SUPERVISE', 'SUPERVISED', 'ACTUAL'];
    const codes = [...baseCodes, 'NOT_APPLICABLE'];

    return codes
      .filter((code) => this.standardStatusButtonsConfig[code])
      .map((code) => ({ code, ...this.standardStatusButtonsConfig[code] }))
      .sort(
        (a, b) => this.getStandardStatusOrderIndex(a.code) - this.getStandardStatusOrderIndex(b.code)
      );
  }

  /**
   * Retourne les statuts personnalisés triés selon l'ordre global défini par filter
   * pour respecter l'ordre de la page /settings/folders/manage-task-status
   */
  getOrderedCustomStatuses(): any[] {
    // Retourner les statuts personnalisés triés par filter
    // L'ordre global est déjà respecté dans customStatuses car ils sont triés lors de la récupération
    return this.customStatuses;
  }

  /**
   * Retourne tous les statuts (standards + personnalisés) dans l'ordre global défini par filter
   * pour respecter EXACTEMENT l'ordre de la page /settings/folders/manage-task-status
   *
   * Cette méthode parcourt tous les statuts dans l'ordre défini par filter et ne garde que :
   * - Les statuts standards qui correspondent à des boutons à afficher (PARTIAL, TO_SUPERVISE, SUPERVISED, ACTUAL, NOT_APPLICABLE)
   * - Les statuts personnalisés (avec firm défini)
   *
   * Chaque statut est enrichi avec une propriété 'isStandardButton' pour indiquer s'il doit être affiché comme bouton standard
   */
  getAllStatusesInOrder(): Array<any & { isStandardButton?: boolean; standardButtonCode?: string }> {
    // Si les statuts ne sont pas encore chargés, retourner un tableau vide
    if (!this.allStatuses || this.allStatuses.length === 0) {
      return [];
    }

    // Mapping des codes de statut réels vers les codes d'action standards
    const statusToActionMap: Record<string, string> = {
      'IN_PROGRESS': 'PARTIAL',
      'TO_SUPERVISE': 'TO_SUPERVISE',
      'SUPERVISED': 'SUPERVISED',
      'DONE': 'ACTUAL',
      'NOT_APPLICABLE': 'NOT_APPLICABLE',
    };

    // Récupérer les codes des statuts standards à afficher
    const standardActionCodes = ['PARTIAL', 'TO_SUPERVISE', 'SUPERVISED', 'ACTUAL', 'NOT_APPLICABLE'];

    // Créer une liste combinée de tous les statuts à afficher
    const allStatusesToDisplay: Array<any & { isStandardButton?: boolean; standardButtonCode?: string }> = [];
    const seenStandardActionCodes = new Set<string>();
    const seenCustomCodes = new Set<string>();

    // IMPORTANT: Parcourir tous les statuts dans l'ordre EXACT défini par filter
    // Cet ordre respecte EXACTEMENT l'ordre défini dans /settings/folders/manage-task-status
    // On doit préserver l'ordre original de this.allStatuses qui est déjà trié par filter
    for (const status of this.allStatuses) {
      // Vérifier si ce statut est un statut standard (sans firm) et correspond à un bouton standard à afficher
      const isBaseStatus = !status.firm;
      const actionCode = statusToActionMap[status.code];
      const isStandardButton = isBaseStatus && actionCode && standardActionCodes.includes(actionCode);

      if (isStandardButton) {
        if (seenStandardActionCodes.has(actionCode)) {
          continue;
        }
        seenStandardActionCodes.add(actionCode);
        // C'est un statut standard à afficher comme bouton (ex: IN_PROGRESS -> PARTIAL, DONE -> ACTUAL)
        allStatusesToDisplay.push({
          ...status,
          isStandardButton: true,
          standardButtonCode: actionCode,
        });
      } else if (status.firm) {
        if (actionCode && standardActionCodes.includes(actionCode)) {
          continue;
        }
        if (seenCustomCodes.has(status.code)) {
          continue;
        }
        seenCustomCodes.add(status.code);
        // C'est un statut personnalisé (ex: "Rejeté", "test")
        // On l'ajoute dans l'ordre où il apparaît dans this.allStatuses (trié par filter)
        allStatusesToDisplay.push({
          ...status,
          isStandardButton: false,
        });
      }
      // Les autres statuts (comme WAITING) ne sont pas affichés dans le modal
    }

    return allStatusesToDisplay;
  }

  /**
   * Vérifie si un code de statut est un statut standard
   */
  isStandardStatus(code: string): boolean {
    return ['TO_SUPERVISE', 'SUPERVISED', 'NOT_APPLICABLE', 'WAITING', 'IN_PROGRESS', 'DONE'].includes(code);
  }

  /**
   * Retourne le bouton standard correspondant à un code de statut
   */
  getStandardButtonForCode(code: string): { code: string; labelKey: string; type: 'submit' | 'toggleNotApplicable'; submitStatus?: string } | null {
    const buttons = this.getStandardButtons();
    return buttons.find(btn => btn.code === code) || null;
  }

  handleStandardStatus(button: {
    code: string;
    type: 'submit' | 'toggleNotApplicable';
    submitStatus?: string;
  }): void {
    if (this.isBulkMode) {
      const statusCode = button.submitStatus || (button.type === 'toggleNotApplicable' ? 'NOT_APPLICABLE' : null);
      if (statusCode) {
        this.executeBulkStatus(statusCode);
      }
      return;
    }
    if (button.type === 'toggleNotApplicable') {
      this.changeStatusBetweenWaitingAndNotApplicable();
    } else if (button.submitStatus) {
      this.onSubmit(button.submitStatus);
    }
  }

  getStatusDisplayName(code: string, fallbackKey: string): string {
    const status = this.baseStatuses.find((s: any) => s.code === code);
    return status?.name || this.translate.instant(fallbackKey);
  }

  get isSupervisor(): boolean {
    return this.currentUserTaskActor?.role?.code === 'SUPERVISOR';
  }

  toggleDatePicker(datepicker: NgbInputDatepicker): void {
    if (!this.isCurrentStatusNotApplicable()) {
      datepicker.toggle();
    }
  }

  focusDurationInput(input: HTMLInputElement): void {
    if (this.isCurrentStatusNotApplicable()) {
      return;
    }
    const nativePicker = (input as any).showPicker;
    if (typeof nativePicker === 'function') {
      nativePicker.call(input);
    } else {
      input.focus();
      input.click();
    }
  }

  getStandardButtonLabel(button: { code: string; labelKey: string }): string {
    if (button.code === 'NOT_APPLICABLE' && this.isCurrentStatusNotApplicable()) {
      return this.getStatusDisplayName('WAITING', 'durationModal.waiting');
    }
    return this.getStatusDisplayName(button.code, button.labelKey);
  }

  constructor(
    public translate: TranslateService,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private apiService: ApiService,
    private formDataApiService: FormDataApiService,
    private toastService: ToastService,
    private authService: AuthenticationService<any>,
    private cdr: ChangeDetectorRef,
    private bulkOperationService: BulkOperationService,
    private taskTimeDisplay: TaskTimeDisplayService,
    private calendar: CalendarService
  ) {
    this.form = this.fb.group({
      plannedDate: ['', [Validators.required]],
      duration: ['', [Validators.required]],
      description: [''],
    });
  }

  get formControls() {
    return this.form.controls;
  }

  ngOnInit(): void {
    if (this.calendarEvent) {
      const event = this.calendarEvent;
      const date = new Date(event.startsAt);
      this.form.patchValue({plannedDate:{year:date.getFullYear(),month:date.getMonth()+1,day:date.getDate()}, duration:this.taskTimeDisplay.formatInput(Math.max(0,(new Date(event.endsAt).getTime()-date.getTime())/60000)),description:event.description});
      this.fetchAllStatuses();
      this.isInitialized = true;
      return;
    }
    if (this.isBulkMode) {
      // En mode masse : activer le formulaire immédiatement + charger les statuts
      this.fetchAllStatuses();
      this.initFormWithToday();
      this.isInitialized = true;
      return;
    }

    this.form.disable();
    // Si le statut est NOT_APPLICABLE, garder le formulaire désactivé
    if (this.isCurrentStatusNotApplicable()) {
      // Le formulaire reste désactivé, seul le bouton "En attente" sera actif
    }
    this.fetchTaskActors();
    if (this.task) {
      this.initForm();
    }
    // Toujours tenter de récupérer l'ID du temps de réalisation pour éviter les erreurs "not found"
    this.fetchPlannedTaskTime();
    // Récupérer TOUS les statuts en un seul appel, exactement comme dans /settings/folders/manage-task-status
    this.fetchAllStatuses();
    this.isInitialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isBulkMode) return;
    // Si la tâche change et que le composant est déjà initialisé, mettre à jour le formulaire
    // Cela permet de recalculer la durée restante si computedDuration a été mis à jour
    if (this.isInitialized && changes['task'] && !changes['task'].firstChange && this.task) {
      this.initForm();
    }
  }

  fetchPlannedTaskTime(): void {
      this.subscription.add(
        this.apiService
          .getAll('task_times', 1, 1, {
            task: this.task.id,
            timeType: 'PLANNED',
          })
          .subscribe({
            next: (response: TaskTimeListResponse) => {
              if (response.member.length) {
                this.plannedTaskTime = response.member[0];
                this.taskTimePlannedId = response.member[0].id;
              }
            },
          })
      );
    }

  fetchTaskActors(): void {
    this.subscription.add(
      this.apiService.getAll(`tasks/${this.task.id}/actors`).subscribe({
        next: (response: TaskActorListResponse) => {
          this.handleFetchTaskActorsSucess(response);
        },
      })
    );
  }

  handleFetchTaskActorsSucess(response: TaskActorListResponse): void {
    this.taskActors = response['hydra:member'] || [];

    const currentUserActor = this.taskActors.find(
      (actor: TaskActor) =>
        actor.user.id === this.authService.currentUserValue?.id
    );
    if (currentUserActor) {
      this.currentUserTaskActor = currentUserActor;
      // Ne pas activer le formulaire si le statut est NOT_APPLICABLE
      if (!this.isCurrentStatusNotApplicable()) {
        this.form.enable();
      }
    }

    // Vérifier si l'utilisateur actuel est superviseur (assigné ou non)
    const currentUserSupervisorActor = this.taskActors.find(
      (actor: TaskActor) =>
        actor.user.id === this.authService.currentUserValue?.id &&
        actor.role.code === 'SUPERVISOR'
    );
    this.isCurrentUserSupervisor = !!currentUserSupervisorActor;

    // Récupérer le rôle EXECUTOR et l'ancien assigné pour les utilisateurs non assignés
    const executorActor = this.taskActors.find(
      (actor: TaskActor) => actor.role.code === 'EXECUTOR'
    );
    if (executorActor) {
      this.executorRoleId = executorActor.role['@id'];
      this.originalExecutor = executorActor;
    }

    // Activer le formulaire pour les utilisateurs non assignés qui ne sont pas superviseurs
    // Mais pas si le statut est NOT_APPLICABLE
    if (!currentUserActor && !this.isCurrentUserSupervisor && !this.isCurrentStatusNotApplicable()) {
      this.form.enable();
    }
  }

  /**
   * Initialise le formulaire avec la date du jour en mode masse
   * (pas de tâche disponible, on pré-remplit juste la date)
   */
  initFormWithToday(): void {
    const today = new Date();
    const todayDateStruct = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
    this.form.patchValue({ plannedDate: todayDateStruct, duration: this.taskTimeDisplay.formatInput(0), description: '' });
    this.form.enable();
  }

  initForm(): void {
    // Recalculer la durée restante à chaque fois que initForm est appelé
    // pour s'assurer d'avoir la dernière valeur de computedDuration
    const duration = this.planningTask?.duration ?? (this.task.initialDuration - (this.task.computedDuration || 0));

    // Obtenir la date du jour au format NgbDateStruct
    const today = this.planningTask?.startTime ? new Date(this.planningTask.startTime) : new Date();
    const todayDateStruct = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };

    this.form.patchValue({
      plannedDate: todayDateStruct,
      duration: duration > 0 ? this.taskTimeDisplay.formatInput(duration) : this.taskTimeDisplay.formatInput(0),
      description: this.task.description,
    });
  }

  /**
   * Met à jour la tâche locale avec la durée validée et recalcule le formulaire
   */
  updateTaskWithValidatedDuration(validatedDuration: number): void {
    if (validatedDuration > 0 && this.task) {
      // Mettre à jour computedDuration sur la référence de la tâche
      const currentComputedDuration = this.task.computedDuration || 0;
      this.task.computedDuration = currentComputedDuration + validatedDuration;

      // Recalculer et mettre à jour le formulaire avec la nouvelle durée restante
      this.initForm();
    }
  }

  onSubmit(status: string): void {
    if (this.isBulkMode) {
      this.executeBulkStatus(status);
      return;
    }
    if (this.calendarEvent) { this.saveCalendarStatus(this.mapActionToStatusCode(status), true); return; }
    this.submitted = true;
    this.isSaving = true;
    if (this.form.invalid) {
      this.isSaving = false;
      return;
    }
    const queryParams = this.buildQueryParams(status);
    // Stocker le statut original pour l'émettre dans l'événement
    (queryParams as any).originalStatus = status;
    this.handleSubmit(queryParams);
  }

  private saveCalendarStatus(status: string, recordTime: boolean): void {
    if (!this.calendarEvent || this.isSaving) { return; }
    this.submitted = true;
    if (recordTime && this.form.invalid) { return; }
    const start = planningDateRange(this.getPlannedDate(), this.getDuration(), this.calendarEvent.startsAt).startTime;
    this.isSaving = true;
    const update = {status, ...(recordTime ? {time:{startTime:start,duration:this.getDuration(),description:String(this.form.get('description')?.value ?? '')}} : {}), ...(this.calendarComment.trim() ? {comment:this.calendarComment} : {})};
    this.subscription.add(this.calendar.qualify(this.calendarEvent.id, update).subscribe({
      next: event => { this.isSaving = false; this.sucessTaskTimeCreation.emit({calendarEvent:event,status:event.status}); this.closeModal(); },
      error: () => { this.isSaving = false; this.toastService.show({message:this.translate.instant('calendar.error'),classname:'bg-danger text-light'}); },
    }));
  }

  buildQueryParams(status: string): CreateTaskTimeQueryParams {
    const plannedDateStr = this.getPlannedDate();
    const duration = this.getDuration();
    const { action, timeType } = this.getActionAndType(status);

    // Si l'utilisateur n'est pas assigné, utiliser le rôle EXECUTOR de la tâche
    let roleId = this.currentUserTaskActor?.role['@id'] || this.executorRoleId;

    const utcRange = this.buildUtcDateRange(
      plannedDateStr || new Date().toISOString().split('T')[0],
      duration
    );

    // Ordre important : startTime et endTime doivent être définis avant timeType
    return {
      task: this.task['@id'],
      role: roleId,
      startTime: utcRange.startTime,
      endTime: utcRange.endTime,
      timeType,
      duration,
      description: this.form.get('description')?.value || '',
      action,
    };
  }

  getPlannedDate(): string {
    const val = this.form.get('plannedDate')?.value;
    return val ? datePickerToDateString(val) : '';
  }

  private buildUtcDateRange(date: string, durationInMinutes: number): {
    startTime: string;
    endTime: string;
  } {
    return planningDateRange(date, durationInMinutes, this.planningTask?.startTime ?? this.plannedTaskTime?.startTime);
  }

  getDuration(): number {
    const val = this.form.get('duration')?.value;
    return val ? this.taskTimeDisplay.parseInput(val) : 0;
  }

  /**
   * Récupère TOUS les statuts en un seul appel, exactement comme dans /settings/folders/manage-task-status
   * pour garantir le même ordre d'affichage
   */
  fetchAllStatuses(): void {
    this.subscription.add(
      this.apiService
        .getAll('task_statuses', 1, 100, {
          'order[filter]': 'ASC',
        })
        .subscribe({
          next: (response: any) => {
            const statusList = response.member || response['hydra:member'] || [];

            // Trier par filter pour garantir l'ordre (même si l'API le fait déjà)
            // Utiliser une fonction de tri stable pour préserver l'ordre en cas d'égalité
            this.allStatuses = [...statusList].sort(
              (a: any, b: any) => {
                const filterA = a.filter ?? 999;
                const filterB = b.filter ?? 999;
                // Si les filter sont égaux, conserver l'ordre original
                if (filterA === filterB) {
                  return 0;
                }
                return filterA - filterB;
              }
            );

            // Séparer les statuts standards et personnalisés pour les besoins internes
            this.baseStatuses = this.allStatuses.filter(
              (status: any) => !status.firm
            );
            this.customStatuses = this.allStatuses.filter(
              (status: any) => status.firm
            );

            // Mettre à jour statusOrder avec tous les statuts
            this.statusOrder = new Map(
              this.allStatuses.map((status: any) => [
                status.code,
                status.filter ?? 999,
              ])
            );

            // Forcer la détection des changements pour mettre à jour le template
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Erreur lors de la récupération des statuts:', error);
          },
        })
    );
  }

  getActionAndType(status: string): {
    action: string | null;
    timeType: string;
  } {
    // Vérifier si c'est un statut personnalisé (avec firm défini)
    const customStatus = this.customStatuses.find((s: any) => s.code === status);
    if (customStatus && customStatus.firm) {
      // Pour les statuts personnalisés, utiliser le code comme action (comme TO_SUPERVISE)
      return { action: status, timeType: 'PARTIAL' };
    }

    // Statuts standards
    if (status === 'SUPERVISED')
      return { action: 'SUPERVISED', timeType: 'PARTIAL' };
    if (status === 'TO_SUPERVISE')
      return { action: 'TO_SUPERVISE', timeType: 'PARTIAL' };
    if (status === 'ACTUAL') return { action: 'DONE', timeType: 'ACTUAL' };
    return { action: null, timeType: 'PARTIAL' };
  }

  handleSubmit(queryParams: CreateTaskTimeQueryParams): void {
    // Vérifier si l'utilisateur est non-assigné
    const isUnassignedUser = !this.currentUserTaskActor;

    if (isUnassignedUser && this.originalExecutor) {
      // Validation par un utilisateur non-assigné
      // Si le statut est DONE, le non-assigné devient assigné (indépendamment de la durée)
      if (queryParams.action === 'DONE') {
        // Quand un non-assigné marque DONE, il devient assigné et on met à jour le task_time existant
        this.handleUnassignedUserMarksDone(queryParams);
      } else {
        // Pour les autres statuts, calculer le temps restant pour déterminer si c'est une validation complète ou partielle
        this.handleCompleteValidationWithoutAssigneeChange(queryParams);

        // if (remainingDuration <= 0) {
        //   // Validation complète mais statut "En cours" : transfert du temps planifié SANS changement d'assigné
        //   this.handleCompleteValidationWithoutAssigneeChange(queryParams);
        // } else {
        //   // Validation partielle : partage proportionnel du temps planifié (pas de changement d'assigné)
        //   this.handlePartialValidationByUnassigned(queryParams);
        // }
      }
    } else {
      // Comportement normal pour les utilisateurs assignés
      if (queryParams.action && queryParams.action === 'DONE') {
        // Déclarer le temps réalisé sans écraser le temps planifié/en cours existant
        this.fetchTaskTimes(queryParams);
      } else {
        this.fetchTaskTimes(queryParams);
      }
    }
  }

  fetchUpdateTask(queryParams: CreateTaskTimeQueryParams): void {
    console.log("this.planningTask", this.planningTask)
    const targetId = this.planningTask?.id || this.taskTimePlannedId;
    if (!targetId) {
      this.toastService.show({
        message:
          'Une erreur est survenue lors de la mise à jour de la tâche: tâche non trouvée.',
        classname: 'bg-danger text-light',
        delay: 15000,
      });
      this.isSaving = false;
      return;
    }
    const updateTaskQueryParams: UpdateTaskTimeQueryParams = {
      id: targetId,
      ...queryParams,
    };
    // Récupérer le statut original depuis queryParams ou déterminer le statut final
    const originalStatus = (queryParams as any).originalStatus;
    let finalStatus = this.task?.status?.code || 'WAITING';

    if (originalStatus) {
      // Si on a le statut original, l'utiliser directement
      if (originalStatus === 'ACTUAL') {
        finalStatus = 'DONE';
      } else if (originalStatus === 'PARTIAL') {
        // PARTIAL signifie "En cours" - chercher le statut "En cours" dans les statuts disponibles
        const currentStatus = this.task?.status?.code;
        // Chercher un statut personnalisé "En cours" dans customStatuses
        const inProgressCustomStatus = this.customStatuses.find(s =>
          s.name?.toLowerCase().includes('cours') || s.code === 'IN_PROGRESS'
        );

        if (inProgressCustomStatus) {
          // Utiliser le statut personnalisé "En cours" trouvé
          finalStatus = inProgressCustomStatus.code;
        } else if (currentStatus === 'IN_PROGRESS') {
          // Si déjà "En cours" (IN_PROGRESS standard), garder le statut actuel
          finalStatus = currentStatus;
        } else {
          // Sinon, essayer d'utiliser IN_PROGRESS standard, ou garder le statut actuel
          finalStatus = 'IN_PROGRESS';
        }
      } else {
        // Pour les autres statuts (TO_SUPERVISE, SUPERVISED, ou statuts personnalisés), utiliser le statut original
        finalStatus = originalStatus;
      }
    } else if (queryParams.action === 'DONE') {
      finalStatus = 'DONE';
    } else if (queryParams.action === 'TO_SUPERVISE') {
      finalStatus = 'TO_SUPERVISE';
    } else if (queryParams.action === 'SUPERVISED') {
      finalStatus = 'SUPERVISED';
    } else if (queryParams.action && queryParams.action !== 'SUPERVISED' && queryParams.action !== 'TO_SUPERVISE') {
      // Si action contient un code de statut personnalisé
      finalStatus = queryParams.action;
    }

    this.subscription.add(
      this.apiService.update('task_times', updateTaskQueryParams).subscribe({
        next: (response) => {
          const plannedDate = planningLocalDay(queryParams.startTime);
          // Inclure le statut dans la réponse
          // Construire la tâche mise à jour avec le nouveau statut et computedDuration recalculé
          const updatedTask = this.buildUpdatedTask(finalStatus, queryParams.duration, {
            plannedDate,
          });
          const eventData = this.buildTaskTimeEvent(
            response,
            queryParams,
            finalStatus,
            updatedTask
          );
          this.sucessTaskTimeCreation.emit(eventData);
          this.closeModal();
        },
        error: () => {
          this.isSaving = false;
        },
      })
    );
  }

  /**
   * Construit une tâche mise à jour avec le nouveau statut et le computedDuration recalculé
   */
  private buildUpdatedTask(finalStatus: string, durationAdded: number = 0, additionalUpdates: any = {}): TaskFormPlanningDto {
    const newComputedDuration = (this.task.computedDuration || 0) + durationAdded;

    // Mettre à jour computedDuration sur la référence originale de la tâche
    if (durationAdded > 0) {
      this.task.computedDuration = newComputedDuration;
      // Recalculer le formulaire avec la nouvelle durée restante
      this.initForm();
    }

    // Si le statut est DONE, ajouter completedAt
    const completedAt = finalStatus === 'DONE' ? new Date().toISOString() : (this.task.completedAt || null);

    const fullStatus = this.allStatuses?.find((s: any) => s.code === finalStatus);
    const statusObj = fullStatus ? { ...fullStatus } : { code: finalStatus };

    return {
      ...this.task,
      status: statusObj as any,
      computedDuration: newComputedDuration,
      completedAt: completedAt,
      ...additionalUpdates
    };
  }

  private buildTaskTimeEvent(
    response: any,
    queryParams: CreateTaskTimeQueryParams,
    finalStatus: string,
    updatedTask: TaskFormPlanningDto
  ): any {
    return {
      ...response,
      status: finalStatus,
      taskId: this.task.id,
      duration: queryParams.duration,
      startTime: queryParams.startTime,
      endTime: queryParams.endTime,
      timeType: queryParams.timeType,
      task: {
        ...updatedTask,
        plannedDate: planningLocalDay(queryParams.startTime),
      },
    };
  }

  /**
   * Détermine le statut final à partir de queryParams
   * Utilisé pour inclure le statut dans les événements émis
   */
  private determineFinalStatus(queryParams: CreateTaskTimeQueryParams): string {
    const originalStatus = (queryParams as any).originalStatus;
    let finalStatus = this.task?.status?.code || 'WAITING';

    if (originalStatus) {
      // Si on a le statut original, l'utiliser directement
      if (originalStatus === 'ACTUAL') {
        finalStatus = 'DONE';
      } else if (originalStatus === 'PARTIAL') {
        // PARTIAL signifie "En cours" - chercher le statut "En cours" dans les statuts disponibles
        const currentStatus = this.task?.status?.code;
        // Chercher un statut personnalisé "En cours" dans customStatuses
        const inProgressCustomStatus = this.customStatuses.find(s =>
          s.name?.toLowerCase().includes('cours') || s.code === 'IN_PROGRESS'
        );

        if (inProgressCustomStatus) {
          // Utiliser le statut personnalisé "En cours" trouvé
          finalStatus = inProgressCustomStatus.code;
        } else if (currentStatus === 'IN_PROGRESS') {
          // Si déjà "En cours" (IN_PROGRESS standard), garder le statut actuel
          finalStatus = currentStatus;
        } else {
          // Sinon, essayer d'utiliser IN_PROGRESS standard, ou garder le statut actuel
          finalStatus = 'IN_PROGRESS';
        }
      } else {
        // Pour les autres statuts (TO_SUPERVISE, SUPERVISED, ou statuts personnalisés), utiliser le statut original
        finalStatus = originalStatus;
      }
    } else if (queryParams.action === 'DONE') {
      finalStatus = 'DONE';
    } else if (queryParams.action === 'TO_SUPERVISE') {
      finalStatus = 'TO_SUPERVISE';
    } else if (queryParams.action === 'SUPERVISED') {
      finalStatus = 'SUPERVISED';
    } else if (queryParams.action && queryParams.action !== 'SUPERVISED' && queryParams.action !== 'TO_SUPERVISE') {
      // Si action contient un code de statut personnalisé
      finalStatus = queryParams.action;
    }

    return finalStatus;
  }

  fetchTaskTimes(queryParams: CreateTaskTimeQueryParams): void {
    // Récupérer le statut original depuis queryParams ou déterminer le statut final
    const finalStatus = this.determineFinalStatus(queryParams);

    if (queryParams.action === 'DONE') {
      this.subscription.add(
        this.apiService
          .getAll('task_times', 1, 1, {
            task: this.task.id,
            timeType: 'ACTUAL',
          })
          .subscribe({
            next: (response: TaskTimeListResponse) => {
              const existingActualTime = response.member[0];
              if (existingActualTime?.id) {
                this.updateTaskTime(existingActualTime.id, queryParams, finalStatus);
                return;
              }
              this.createTaskTime(queryParams, finalStatus);
            },
            error: () => this.createTaskTime(queryParams, finalStatus),
          })
      );
      return;
    }

    this.createTaskTime(queryParams, finalStatus);
  }

  private createTaskTime(queryParams: CreateTaskTimeQueryParams, finalStatus: string): void {
    this.subscription.add(
      this.apiService.create('task_times', queryParams).subscribe({
        next: (response) => {
          const plannedDate = planningLocalDay(queryParams.startTime);
          // Inclure le statut dans la réponse
          // Construire la tâche mise à jour avec le nouveau statut et computedDuration recalculé
          const updatedTask = this.buildUpdatedTask(finalStatus, queryParams.duration, {
            plannedDate,
          });
          const eventData = this.buildTaskTimeEvent(
            response,
            queryParams,
            finalStatus,
            updatedTask
          );
          this.sucessTaskTimeCreation.emit(eventData);
          this.closeModal();
        },
        error: () => {
          this.isSaving = false;
        },
      })
    );
  }

  private updateTaskTime(
    id: string,
    queryParams: CreateTaskTimeQueryParams,
    finalStatus: string
  ): void {
    this.subscription.add(
      this.apiService.update('task_times', { id, ...queryParams }).subscribe({
        next: (response) => {
          const plannedDate = planningLocalDay(queryParams.startTime);
          const updatedTask = this.buildUpdatedTask(finalStatus, queryParams.duration, {
            plannedDate,
          });
          const eventData = this.buildTaskTimeEvent(
            response,
            queryParams,
            finalStatus,
            updatedTask
          );
          this.sucessTaskTimeCreation.emit(eventData);
          this.closeModal();
        },
        error: () => {
          this.isSaving = false;
        },
      })
    );
  }

  closeModal(result?: any): void {
    this.activeModal.close(result ?? null);
  }

  onCommentAdded(): void {
    // Ne pas déclencher la validation de la tâche lors de l'ajout d'un commentaire
    // Le commentaire est géré indépendamment de la validation de la tâche
    console.log('Commentaire ajouté pour la tâche:', this.task?.id);
  }

  /**
   * Gère le cas où un utilisateur non-assigné marque une tâche à DONE
   * - Change l'assigné : le non-assigné devient l'assigné principal (indépendamment de la durée)
   * - Met à jour le task_time planifié existant (transfère au nouveau validateur)
   * - Met à jour le task_time réel existant avec action DONE et timeType ACTUAL (ou crée un nouveau)
   */
  handleUnassignedUserMarksDone(queryParams: CreateTaskTimeQueryParams): void {
    const currentUserId = this.authService.currentUserValue?.id;
    if (!currentUserId || !this.originalExecutor || !this.plannedTaskTime) {
      this.toastService.show({
        message: 'Une erreur est survenue : informations manquantes.',
        classname: 'bg-danger text-light',
        delay: 5000,
      });
      this.isSaving = false;
      return;
    }

    const currentUserIri = `/api/users/${currentUserId}`;
    const plannedDuration = this.plannedTaskTime.duration || this.task.initialDuration;
    let plannedDate = this.getPlannedDate();

    // Si la date n'est pas définie, utiliser la date du temps planifié ou la date planifiée de la tâche
    if (!plannedDate && this.plannedTaskTime?.startTime) {
      plannedDate = this.plannedTaskTime.startTime.split('T')[0];
    } else if (!plannedDate && this.task.plannedDate) {
      plannedDate = this.task.plannedDate;
    }

    if (!plannedDate) {
      this.toastService.show({
        message: 'Une date de planification est requise.',
        classname: 'bg-danger text-light',
        delay: 5000,
      });
      this.isSaving = false;
      return;
    }

    // 1. Changer l'assigné : le non-assigné devient l'assigné principal
    const changeAssigneeParams = {
      user: currentUserIri,
      role: 'EXECUTOR',
      plannedDate: plannedDate,
      duration: plannedDuration,
    };

    this.subscription.add(
      this.apiService
        .create(`tasks/${this.task.id}/change_assignee`, changeAssigneeParams)
        .subscribe({
          next: () => {
            // 2. Créer un temps réel sans écraser le temps planifié existant
            const actualTimeParams: CreateTaskTimeQueryParams = {
              task: this.task['@id'],
              role: this.executorRoleId,
              user: currentUserIri,
              startTime: queryParams.startTime,
              endTime: queryParams.endTime,
              timeType: 'ACTUAL',
              duration: queryParams.duration,
              description: queryParams.description || '',
              action: 'DONE',
            };

            this.subscription.add(
              this.apiService.create('task_times', actualTimeParams).subscribe({
                next: (response) => {
                  const finalStatus = this.determineFinalStatus(queryParams);
                  // Construire la tâche mise à jour avec le nouvel assigné, le nouveau statut et computedDuration recalculé
                  const updatedActors = this.task.actors?.map((actor: any) => {
                    if (actor.role?.code === 'EXECUTOR') {
                      return {
                        ...actor,
                        user: {
                          '@id': currentUserIri,
                          id: currentUserId,
                          firstName: this.authService.currentUserValue?.firstName || '',
                          lastName: this.authService.currentUserValue?.lastName || ''
                        }
                      };
                    }
                    return actor;
                  }) || [];
                  const updatedTask = this.buildUpdatedTask(finalStatus, queryParams.duration, { actors: updatedActors });

                  const eventData = this.buildTaskTimeEvent(
                    response,
                    queryParams,
                    finalStatus,
                    updatedTask
                  );
                  this.sucessTaskTimeCreation.emit(eventData);
                  this.closeModal();
                },
                error: () => {
                  this.isSaving = false;
                  this.toastService.show({
                    message: 'Erreur lors de la mise à jour du task_time.',
                    classname: 'bg-danger text-light',
                    delay: 5000,
                  });
                },
              })
            );
          },
          error: () => {
            this.isSaving = false;
            this.toastService.show({
              message: 'Erreur lors du changement d\'assigné.',
              classname: 'bg-danger text-light',
              delay: 5000,
            });
          },
        })
    );
  }

  /**
   * Gère la validation complète par un utilisateur non-assigné avec changement d'assigné
   * - Change l'assigné vers le validateur (uniquement si le statut est DONE)
   * - Transfère tout le temps planifié vers le nouveau validateur
   * - Crée le temps correspondant (partiel si PARTIAL, réel si ACTUAL)
   * Utilisé uniquement quand le statut choisi est "Terminé" (DONE)
   * @deprecated Utiliser handleUnassignedUserMarksDone à la place
   */
  handleCompleteValidationByUnassigned(queryParams: CreateTaskTimeQueryParams): void {
    const currentUserId = this.authService.currentUserValue?.id;
    if (!currentUserId || !this.originalExecutor || !this.plannedTaskTime) {
      this.toastService.show({
        message: 'Une erreur est survenue : informations manquantes.',
        classname: 'bg-danger text-light',
        delay: 5000,
      });
      this.isSaving = false;
      return;
    }

    const currentUserIri = `/api/users/${currentUserId}`;
    const plannedDuration = this.plannedTaskTime.duration || this.task.initialDuration;
    let plannedDate = this.getPlannedDate();

    // Si la date n'est pas définie, utiliser la date du temps planifié ou la date planifiée de la tâche
    if (!plannedDate && this.plannedTaskTime?.startTime) {
      plannedDate = this.plannedTaskTime.startTime.split('T')[0]; // Extraire la date (YYYY-MM-DD) de l'ISO string
    } else if (!plannedDate && this.task.plannedDate) {
      plannedDate = this.task.plannedDate;
    }

    if (!plannedDate) {
      this.toastService.show({
        message: 'Une date de planification est requise.',
        classname: 'bg-danger text-light',
        delay: 5000,
      });
      this.isSaving = false;
      return;
    }

    // 1. Changer l'assigné vers le validateur
    const changeAssigneeParams = {
      user: currentUserIri,
      role: 'EXECUTOR',
      plannedDate: plannedDate,
      duration: plannedDuration,
    };

    this.subscription.add(
      this.apiService
        .create(`tasks/${this.task.id}/change_assignee`, changeAssigneeParams)
        .subscribe({
          next: () => {
            // 2. Mettre à jour le temps planifié avec le nouveau validateur
            const plannedDateStr = this.getPlannedDate() || this.plannedTaskTime.startTime?.split('T')[0] || new Date().toISOString().split('T')[0];
            const plannedStartTime = new Date(plannedDateStr);
            const plannedEndTime = new Date(plannedStartTime.getTime() + plannedDuration * 60000);

            const updatePlannedParams: UpdateTaskTimeQueryParams = {
              id: this.taskTimePlannedId,
              task: this.task['@id'],
              role: this.executorRoleId,
              user: currentUserIri,
              startTime: plannedStartTime.toISOString(),
              endTime: plannedEndTime.toISOString(),
              timeType: 'PLANNED',
              duration: plannedDuration,
              description: this.plannedTaskTime.description || '',
              action: null,
            };

            this.subscription.add(
              this.apiService.update('task_times', updatePlannedParams).subscribe({
                next: () => {
                  // 3. Créer le temps correspondant (partiel si PARTIAL, réel si ACTUAL)
                  // Respecter le timeType et l'action choisis par l'utilisateur
                  // IMPORTANT: Utiliser directement queryParams.timeType et queryParams.action
                  // pour respecter le choix de l'utilisateur, même si la date de planification change
                  // Ne pas forcer 'ACTUAL' par défaut car cela changerait le statut à DONE
                  const timeType = queryParams.timeType || 'PARTIAL'; // Fallback vers PARTIAL si undefined (pas ACTUAL)
                  const action = queryParams.action; // Utiliser directement l'action choisie

                  const actualTimeParams: CreateTaskTimeQueryParams = {
                    task: this.task['@id'],
                    role: this.executorRoleId,
                    user: currentUserIri,
                    startTime: queryParams.startTime,
                    endTime: queryParams.endTime,
                    timeType: timeType,
                    duration: queryParams.duration,
                    description: queryParams.description,
                    action: action,
                  };

                  this.subscription.add(
                    this.apiService.create('task_times', actualTimeParams).subscribe({
                      next: (response) => {
                        // Déterminer le statut final et inclure dans l'événement pour permettre le rafraîchissement automatique
                        const finalStatus = this.determineFinalStatus(queryParams);

                        // Mettre à jour computedDuration sur la référence de la tâche avant de construire updatedTask
                        this.updateTaskWithValidatedDuration(queryParams.duration);

                        // Construire la tâche mise à jour avec le nouveau statut
                        const updatedTask = {
                          ...this.task,
                          status: (this.allStatuses?.find((s: any) => s.code === finalStatus) ?? { code: finalStatus })
                        };
                        const eventData = this.buildTaskTimeEvent(
                          response,
                          queryParams,
                          finalStatus,
                          updatedTask as TaskFormPlanningDto
                        );
                        this.sucessTaskTimeCreation.emit(eventData);
                        this.closeModal();
                      },
                      error: () => {
                        this.isSaving = false;
                        this.toastService.show({
                          message: 'Erreur lors de la création du temps.',
                          classname: 'bg-danger text-light',
                          delay: 5000,
                        });
                      },
                    })
                  );
                },
                error: () => {
                  this.isSaving = false;
                  this.toastService.show({
                    message: 'Erreur lors du transfert du temps planifié.',
                    classname: 'bg-danger text-light',
                    delay: 5000,
                  });
                },
              })
            );
          },
          error: () => {
            this.isSaving = false;
            this.toastService.show({
              message: 'Erreur lors du changement d\'assigné.',
              classname: 'bg-danger text-light',
              delay: 5000,
            });
          },
        })
    );
  }

  /**
   * Gère la validation complète par un utilisateur non-assigné SANS changer l'assigné
   * - NE CHANGE PAS l'assigné (l'ancien assigné reste assigné principal)
   * - Crée le temps correspondant (partiel si PARTIAL, réel si ACTUAL)
   * Utilisé quand le statut est "En cours" (pas DONE) mais que tout le temps planifié est validé
   */
  handleCompleteValidationWithoutAssigneeChange(queryParams: CreateTaskTimeQueryParams): void {
    const currentUserId = this.authService.currentUserValue?.id;
    if (!currentUserId || !this.originalExecutor || !this.plannedTaskTime) {
      this.toastService.show({
        message: 'Une erreur est survenue : informations manquantes.',
        classname: 'bg-danger text-light',
        delay: 5000,
      });
      this.isSaving = false;
      return;
    }

    const currentUserIri = `/api/users/${currentUserId}`;

    // Créer le temps correspondant (partiel si PARTIAL, réel si ACTUAL)
    const timeType = queryParams.timeType || 'PARTIAL';
    const action = queryParams.action;

    const actualTimeParams: CreateTaskTimeQueryParams = {
      task: this.task['@id'],
      role: this.executorRoleId,
      user: currentUserIri,
      startTime: queryParams.startTime,
      endTime: queryParams.endTime,
      timeType: timeType,
      duration: queryParams.duration,
      description: queryParams.description,
      action: action,
    };

    this.subscription.add(
      this.apiService.create('task_times', actualTimeParams).subscribe({
        next: (response) => {
          const finalStatus = this.determineFinalStatus(queryParams);

          // Mettre à jour computedDuration sur la référence de la tâche avant de construire updatedTask
          this.updateTaskWithValidatedDuration(queryParams.duration);

          // Construire la tâche mise à jour avec le nouveau statut
          const updatedTask = {
            ...this.task,
            status: (this.allStatuses?.find((s: any) => s.code === finalStatus) ?? { code: finalStatus })
          };
          const eventData = this.buildTaskTimeEvent(
            response,
            queryParams,
            finalStatus,
            updatedTask as TaskFormPlanningDto
          );
          this.sucessTaskTimeCreation.emit(eventData);
          this.closeModal();
        },
        error: () => {
          this.isSaving = false;
          this.toastService.show({
            message: 'Erreur lors de la création du temps.',
            classname: 'bg-danger text-light',
            delay: 5000,
          });
        },
      })
    );
  }

  /**
   * Gère la validation partielle par un utilisateur non-assigné
   * - NE CHANGE PAS l'assigné de la tâche
   * - Partage proportionnellement le temps planifié entre l'ancien assigné et le validateur
   * - Crée un nouveau temps planifié pour le validateur
   * - Crée le temps correspondant pour le validateur (partiel si PARTIAL, réel si ACTUAL)
   * Exemple : Michael valide 1h sur 3h → Michael 1h planifié + 1h partiel/réel, Yair 2h planifié
   */
  handlePartialValidationByUnassigned(queryParams: CreateTaskTimeQueryParams): void {
    const currentUserId = this.authService.currentUserValue?.id;
    if (!currentUserId || !this.originalExecutor || !this.plannedTaskTime) {
      this.toastService.show({
        message: 'Une erreur est survenue : informations manquantes.',
        classname: 'bg-danger text-light',
        delay: 5000,
      });
      this.isSaving = false;
      return;
    }

    const currentUserIri = `/api/users/${currentUserId}`;
    const originalExecutorIri = this.originalExecutor.user['@id'] || `/api/users/${this.originalExecutor.user.id}`;
    const validatedDuration = queryParams.duration; // Temps validé par le non-assigné
    const totalPlannedDuration = this.plannedTaskTime.duration || this.task.initialDuration;
    const remainingDuration = totalPlannedDuration - validatedDuration;

    // 0. D'abord, ajouter l'utilisateur comme acteur EXECUTOR pour qu'il puisse déclarer du temps
    // On utilise change_assignee pour ajouter l'utilisateur comme acteur temporairement
    const plannedDateStr = this.getPlannedDate() || this.plannedTaskTime.startTime?.split('T')[0] || new Date().toISOString().split('T')[0];

    this.subscription.add(
      this.apiService
        .create(`tasks/${this.task.id}/change_assignee`, {
          user: currentUserIri,
          role: 'EXECUTOR',
          plannedDate: plannedDateStr,
          duration: totalPlannedDuration,
        })
        .subscribe({
          next: () => {
            // Maintenant que l'utilisateur est acteur, on peut créer les temps
            // Après avoir créé les temps, on remettra l'ancien assigné si nécessaire
            this.proceedWithPartialValidation(queryParams, currentUserIri, originalExecutorIri, validatedDuration, remainingDuration, totalPlannedDuration, plannedDateStr);
          },
          error: () => {
            this.isSaving = false;
            this.toastService.show({
              message: 'Erreur lors de l\'ajout de l\'utilisateur comme acteur.',
              classname: 'bg-danger text-light',
              delay: 5000,
            });
          },
        })
    );
  }

  /**
   * Continue la validation partielle après avoir ajouté l'utilisateur comme acteur
   */
  proceedWithPartialValidation(
    queryParams: CreateTaskTimeQueryParams,
    currentUserIri: string,
    originalExecutorIri: string,
    validatedDuration: number,
    remainingDuration: number,
    totalPlannedDuration: number,
    plannedDateStr: string
  ): void {
    // 1. Mettre à jour le temps planifié de l'ancien assigné (réduire la durée)
    if (remainingDuration > 0) {
      const originalStartTime = this.plannedTaskTime.startTime ? new Date(this.plannedTaskTime.startTime) : new Date();
      const originalEndTime = new Date(originalStartTime.getTime() + remainingDuration * 60000);

      const updateOriginalPlannedParams: UpdateTaskTimeQueryParams = {
        id: this.taskTimePlannedId,
        task: this.task['@id'],
        role: this.executorRoleId,
        user: originalExecutorIri,
        startTime: originalStartTime.toISOString(),
        endTime: originalEndTime.toISOString(),
        timeType: 'PLANNED',
        duration: remainingDuration,
        description: this.plannedTaskTime.description || '',
        action: null,
      };

      this.subscription.add(
        this.apiService.update('task_times', updateOriginalPlannedParams).subscribe({
          next: () => {
            // 2. Créer le task_time partiel pour le validateur
            // Utiliser le timeType de queryParams (PARTIAL pour "En cours", ACTUAL pour "Terminé")
            // Si la tâche n'est pas terminée (remainingDuration > 0), on doit utiliser PARTIAL
            // pour éviter de clôturer la tâche prématurément
            const timeType = queryParams.timeType || (remainingDuration > 0 ? 'PARTIAL' : 'ACTUAL');
            const actualTimeParams: CreateTaskTimeQueryParams = {
              task: this.task['@id'],
              role: this.executorRoleId,
              user: currentUserIri,
              startTime: queryParams.startTime,
              endTime: queryParams.endTime,
              timeType: timeType,
              duration: validatedDuration,
              description: queryParams.description,
              action: queryParams.action,
            };

            this.subscription.add(
              this.apiService.create('task_times', actualTimeParams).subscribe({
                next: (response) => {
                  // 3. Remettre l'ancien assigné comme assigné principal si nécessaire
                  // (l'utilisateur reste acteur pour pouvoir déclarer du temps)
                  if (remainingDuration > 0) {
                    this.subscription.add(
                      this.apiService
                        .create(`tasks/${this.task.id}/change_assignee`, {
                          user: originalExecutorIri,
                          role: 'EXECUTOR',
                          plannedDate: plannedDateStr,
                          duration: remainingDuration,
                        })
                        .subscribe({
                          next: () => {
                            // Déterminer le statut final et inclure dans l'événement pour permettre le rafraîchissement automatique
                            const finalStatus = this.determineFinalStatus(queryParams);

                            // Mettre à jour computedDuration sur la référence de la tâche avant de construire updatedTask
                            this.updateTaskWithValidatedDuration(validatedDuration);

                            // Construire la tâche mise à jour avec le nouveau statut
                            const updatedTask = {
                              ...this.task,
                              status: (this.allStatuses?.find((s: any) => s.code === finalStatus) ?? { code: finalStatus })
                            };
                            const eventData = this.buildTaskTimeEvent(
                              response,
                              queryParams,
                              finalStatus,
                              updatedTask as TaskFormPlanningDto
                            );
                            this.sucessTaskTimeCreation.emit(eventData);
                            this.closeModal();
                          },
                          error: () => {
                            // Même en cas d'erreur, on émet le succès car les temps sont créés
                            const finalStatus = this.determineFinalStatus(queryParams);

                            // Mettre à jour computedDuration sur la référence de la tâche avant de construire updatedTask
                            this.updateTaskWithValidatedDuration(validatedDuration);

                            const updatedTask = {
                              ...this.task,
                              status: (this.allStatuses?.find((s: any) => s.code === finalStatus) ?? { code: finalStatus })
                            };
                            const eventData = this.buildTaskTimeEvent(
                              response,
                              queryParams,
                              finalStatus,
                              updatedTask as TaskFormPlanningDto
                            );
                            this.sucessTaskTimeCreation.emit(eventData);
                            this.closeModal();
                          },
                        })
                    );
                  } else {
                    // Déterminer le statut final et inclure dans l'événement pour permettre le rafraîchissement automatique
                    const finalStatus = this.determineFinalStatus(queryParams);

                    // Mettre à jour computedDuration sur la référence de la tâche avant de construire updatedTask
                    this.updateTaskWithValidatedDuration(validatedDuration);

                    // Construire la tâche mise à jour avec le nouveau statut
                    const updatedTask = {
                      ...this.task,
                      status: (this.allStatuses?.find((s: any) => s.code === finalStatus) ?? { code: finalStatus })
                    };
                    const eventData = this.buildTaskTimeEvent(
                      response,
                      queryParams,
                      finalStatus,
                      updatedTask as TaskFormPlanningDto
                    );
                    this.sucessTaskTimeCreation.emit(eventData);
                    this.closeModal();
                  }
                },
                error: () => {
                  this.isSaving = false;
                  this.toastService.show({
                    message: 'Erreur lors de la création du task_time partiel.',
                    classname: 'bg-danger text-light',
                    delay: 5000,
                  });
                },
              })
            );
          },
          error: () => {
            this.isSaving = false;
            this.toastService.show({
              message: 'Erreur lors de la mise à jour du temps planifié de l\'ancien assigné.',
              classname: 'bg-danger text-light',
              delay: 5000,
            });
          },
        })
      );
    } else {
      // Si le temps restant est 0 ou négatif, c'est une validation complète
      this.handleCompleteValidationByUnassigned(queryParams);
    }
  }

  // Obtenir le statut actuel de la tâche
  getCurrentTaskStatus(): string {
    return this.calendarEvent?.status ?? this.task?.status?.code ?? 'WAITING';
  }

  // Vérifier si le statut actuel est NOT_APPLICABLE
  isCurrentStatusNotApplicable(): boolean {
    return this.getCurrentTaskStatus() === 'NOT_APPLICABLE';
  }

  // Vérifier si le statut actuel est DONE
  isCurrentStatusDone(): boolean {
    return this.getCurrentTaskStatus() === 'DONE';
  }

  // Changer le statut entre WAITING et NOT_APPLICABLE
  changeStatusBetweenWaitingAndNotApplicable(): void {
    if (this.calendarEvent) { this.saveCalendarStatus(this.calendarEvent.status === 'NOT_APPLICABLE' ? 'WAITING' : 'NOT_APPLICABLE', false); return; }
    this.isSaving = true;
    const currentStatus = this.getCurrentTaskStatus();
    const targetStatus = currentStatus === 'NOT_APPLICABLE' ? 'WAITING' : 'NOT_APPLICABLE';

    // Récupérer l'IRI du statut cible depuis l'API
    this.apiService.getAll('task_statuses', 1, 100, { code: targetStatus }).subscribe({
      next: (response: any) => {
        const statusList = response.member || response['hydra:member'] || [];
        const status = statusList.find((s: any) => s.code === targetStatus);

        if (status) {
          // Mettre à jour le statut de la tâche
          this.apiService.update('tasks', { id: this.task.id, status: status['@id'] || status.id }).subscribe({
            next: () => {
              // Mettre à jour le statut localement dans le modal pour que l'affichage se mette à jour immédiatement
              if (this.task && this.task.status) {
                this.task.status = {
                  ...this.task.status,
                  code: targetStatus,
                  ...(status['@id'] && { '@id': status['@id'] }),
                  ...(status.id && { id: status.id }),
                  ...(status.name && { name: status.name })
                };
              } else if (this.task) {
                this.task.status = {
                  code: targetStatus,
                  ...(status['@id'] && { '@id': status['@id'] }),
                  ...(status.id && { id: status.id }),
                  ...(status.name && { name: status.name })
                } as any;
              }

              // Réinitialiser l'état du formulaire selon le nouveau statut
              if (targetStatus === 'NOT_APPLICABLE') {
                this.form.disable();
              } else {
                // Réactiver le formulaire si nécessaire
                if (this.currentUserTaskActor || (!this.currentUserTaskActor && !this.isCurrentUserSupervisor)) {
                  this.form.enable();
                }
              }

              // Forcer la détection des changements pour mettre à jour le template
              this.cdr.detectChanges();

              this.isSaving = false;
              this.toastService.show({
                message: 'Statut mis à jour avec succès.',
                classname: 'bg-success text-light',
                delay: 3000,
              });
              // Pour un changement de statut uniquement, pas de durée ajoutée
              const updatedTask = this.buildUpdatedTask(targetStatus, 0);
              this.sucessTaskTimeCreation.emit({ action: 'changeStatus', taskId: this.task.id, status: targetStatus, task: updatedTask });
              this.closeModal();
            },
            error: () => {
              this.isSaving = false;
              this.toastService.show({
                message: 'Erreur lors de la mise à jour du statut.',
                classname: 'bg-danger text-light',
                delay: 5000,
              });
            }
          });
        } else {
          this.isSaving = false;
          this.toastService.show({
            message: `Statut ${targetStatus} non trouvé.`,
            classname: 'bg-danger text-light',
            delay: 5000,
          });
        }
      },
      error: () => {
        this.isSaving = false;
        this.toastService.show({
          message: 'Erreur lors de la récupération du statut.',
          classname: 'bg-danger text-light',
          delay: 5000,
        });
      }
    });
  }

  changeStatusToNotApplicable(): void {
    this.isSaving = true;

    // Récupérer l'IRI du statut NOT_APPLICABLE depuis l'API
    this.apiService.getAll('task_statuses', 1, 100, { code: 'NOT_APPLICABLE' }).subscribe({
      next: (response: any) => {
        const statusList = response.member || response['hydra:member'] || [];
        const status = statusList.find((s: any) => s.code === 'NOT_APPLICABLE');

        if (status) {
          // Mettre à jour le statut de la tâche
          this.apiService.update('tasks', { id: this.task.id, status: status['@id'] || status.id }).subscribe({
            next: () => {
              // Mettre à jour le statut localement dans le modal pour que l'affichage se mette à jour immédiatement
              if (this.task && this.task.status) {
                this.task.status = {
                  ...this.task.status,
                  code: 'NOT_APPLICABLE',
                  ...(status['@id'] && { '@id': status['@id'] }),
                  ...(status.id && { id: status.id }),
                  ...(status.name && { name: status.name })
                };
              } else if (this.task) {
                this.task.status = {
                  code: 'NOT_APPLICABLE',
                  ...(status['@id'] && { '@id': status['@id'] }),
                  ...(status.id && { id: status.id }),
                  ...(status.name && { name: status.name })
                } as any;
              }

              // Désactiver le formulaire car le statut est maintenant NOT_APPLICABLE
              this.form.disable();

              // Forcer la détection des changements pour mettre à jour le template
              this.cdr.detectChanges();

              this.isSaving = false;
              this.toastService.show({
                message: 'Statut mis à jour avec succès.',
                classname: 'bg-success text-light',
                delay: 3000,
              });
              this.sucessTaskTimeCreation.emit({ action: 'changeStatus', taskId: this.task.id, status: 'NOT_APPLICABLE' });
              this.closeModal();
            },
            error: () => {
              this.isSaving = false;
              this.toastService.show({
                message: 'Erreur lors de la mise à jour du statut.',
                classname: 'bg-danger text-light',
                delay: 5000,
              });
            }
          });
        } else {
          this.isSaving = false;
          this.toastService.show({
            message: 'Statut non applicable non trouvé.',
            classname: 'bg-danger text-light',
            delay: 5000,
          });
        }
      },
      error: () => {
        this.isSaving = false;
        this.toastService.show({
          message: 'Erreur lors de la récupération du statut.',
          classname: 'bg-danger text-light',
          delay: 5000,
        });
      }
    });
  }

  // ─── Méthodes pour le mode mise à jour en masse ───────────────────────────

  /**
   * Mappe le code d'action du bouton vers le code réel du statut API
   */
  private mapActionToStatusCode(actionCode: string): string {
    const mapping: Record<string, string> = {
      PARTIAL: 'IN_PROGRESS',
      ACTUAL: 'DONE',
      TO_SUPERVISE: 'TO_SUPERVISE',
      SUPERVISED: 'SUPERVISED',
      NOT_APPLICABLE: 'NOT_APPLICABLE',
      WAITING: 'WAITING',
    };
    return mapping[actionCode] || actionCode;
  }

  /**
   * Phase 1 : Prépare la liste des IDs à traiter.
   * - Si bulkTaskIds est fourni (sélection manuelle) → utilise ces IDs directement.
   * - Sinon (mode "tout sélectionner") → récupère depuis deadlines/table avec pagination.
   */
  async fetchBulkIds(): Promise<void> {
    if (this.bulkIdsLoaded) return;

    // Cas 1 : IDs fournis directement par la sélection de lignes
    if (this.bulkTaskIds && this.bulkTaskIds.length > 0) {
      // Déduplication via Set pour éliminer tout doublon en amont
      this.bulkIds = Array.from(new Set(this.bulkTaskIds.map((id) => String(id).trim()).filter(Boolean)));
      this.bulkIdsLoaded = true;
      this.cdr.detectChanges();
      return;
    }

    // Cas 2 : Récupération via l'API (mode "tout cocher")
    this.isFetchingIds = true;
    const seenIds = new Set<string>();

    const BULK_PAGE_SIZE = 300;
    let page = 1;
    let totalPages = 1;

    try {
      do {
        const response = await lastValueFrom(
          this.apiService.getAll('deadlines/table', page, BULK_PAGE_SIZE, this.bulkFilters, [], null, new HttpContext().set(SKIP_SPINNER, true))
        );

        const taskBatches: any[] = (response as any).member?.[1] || [];
        taskBatches.forEach((batch: any) => {
          (batch.taskModelTasks || []).forEach((task: any) => {
            const id = String(task.id ?? '').trim();
            if (id) seenIds.add(id);
          });
        });

        const totalItems = (response as any).totalItems || 0;
        totalPages = Math.ceil(totalItems / BULK_PAGE_SIZE);
        page++;
      } while (page <= totalPages);

      this.bulkIds = Array.from(seenIds);
      this.bulkIdsLoaded = true;
    } catch (error) {
      throw error;
    } finally {
      this.isFetchingIds = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Délègue l'exécution en masse au BulkOperationService non bloquant,
   * puis ferme le modal immédiatement pour libérer l'utilisateur.
   */
  async executeBulkStatus(actionCode: string): Promise<void> {
    if (this.isBulkProcessing) return;

    this.submitted = true;
    this.isBulkProcessing = true;
    this.isFetchingIds = true;
    this.bulkErrors = [];
    this.cdr.detectChanges();

    try {
      // Phase 1 : récupération des IDs (une seule fois)
      if (!this.bulkIdsLoaded) {
        await this.fetchBulkIds();
      }

      if (this.bulkIds.length === 0) {
        this.bulkErrors = ['Aucune tâche trouvée pour les filtres sélectionnés'];
        this.isBulkProcessing = false;
        this.isFetchingIds = false;
        this.cdr.detectChanges();
        return;
      }

      // Résoudre le code de statut réel et son IRI
      const actualStatusCode = this.mapActionToStatusCode(actionCode);
      const statusResponse = await lastValueFrom(
        this.apiService.getAll('task_statuses', 1, 100, { code: actualStatusCode }, [], null, new HttpContext().set(SKIP_SPINNER, true))
      );
      const statusList = (statusResponse as any).member || (statusResponse as any)['hydra:member'] || [];
      const statusObj = statusList.find((s: any) => s.code === actualStatusCode);

      if (!statusObj) {
        this.bulkErrors = [`Statut « ${actualStatusCode} » introuvable`];
        this.isBulkProcessing = false;
        this.isFetchingIds = false;
        this.cdr.detectChanges();
        return;
      }

      const statusIri = statusObj['@id'] || `/api/task_statuses/${statusObj.id}`;
      const durationValue = this.getDuration();
      const plannedDateStr = this.getPlannedDate();
      const hasTimeData = durationValue > 0 && !!plannedDateStr;
      const { action, timeType } = this.getActionAndType(actionCode);
      const description = this.form.get('description')?.value || '';
      const statusName = this.allStatuses.find((s: any) => s.code === actualStatusCode)?.name
        || this.translate.instant('bulkProgressToast.statusFallback');
      const label = this.translate.instant('bulkProgressToast.statusLabel', { name: statusName });

      // Déléguer l'exécution au service non bloquant et fermer immédiatement
      this.bulkOperationService.startBulkStatus({
        taskIds: [...this.bulkIds],
        hasTimeData,
        durationValue,
        plannedDateStr,
        timeType,
        action,
        description,
        statusIri,
        actionCode,
        label,
      });

      this.closeModal({ refresh: true });
    } catch (error: any) {
      this.bulkErrors = [
        error?.error?.['hydra:description'] || error?.message || 'Une erreur inattendue est survenue',
      ];
      this.isBulkProcessing = false;
      this.isFetchingIds = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Délègue l'envoi de commentaire en masse au BulkOperationService non bloquant,
   * puis ferme le modal immédiatement pour libérer l'utilisateur.
   */
  async executeBulkComment(): Promise<void> {
    if (!this.bulkCommentText.trim() || this.isBulkCommentPosting) return;

    this.isBulkCommentPosting = true;
    this.bulkCommentErrors = [];
    this.cdr.detectChanges();

    try {
      // Phase 1 : récupération des IDs (réutilisés si déjà chargés)
      if (!this.bulkIdsLoaded) {
        await this.fetchBulkIds();
      }

      if (this.bulkIds.length === 0) {
        this.bulkCommentErrors = ['Aucune tâche trouvée pour les filtres sélectionnés'];
        this.isBulkCommentPosting = false;
        this.cdr.detectChanges();
        return;
      }

      const label = this.translate.instant('bulkProgressToast.commentLabel');

      // Déléguer l'exécution au service non bloquant et fermer immédiatement
      this.bulkOperationService.startBulkComment({
        taskIds: [...this.bulkIds],
        commentContent: this.bulkCommentText,
        label,
      });

      this.closeModal({ refresh: true });
    } catch (error: any) {
      this.bulkCommentErrors = [
        error?.error?.['hydra:description'] || error?.message || 'Une erreur inattendue est survenue',
      ];
      this.isBulkCommentPosting = false;
      this.cdr.detectChanges();
    }
  }

  // ──────────────────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  toggleComments(): void {
    this.isCommentsExpanded = !this.isCommentsExpanded;
  }

}
